import React, { useMemo } from "react";
import { Box, Grid, HStack, Select, Stack, Text, createListCollection } from "@chakra-ui/react";
import { DashboardInput, DashboardTextarea, DashboardSelectTrigger, DashboardSelectContent, DashboardSelectItem } from "components/utils/FormControls";
import { MdCategory, MdComment, MdSports } from "react-icons/md";
import { FaHandsHelping, FaInfoCircle, FaUserFriends } from "react-icons/fa";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { TagCombobox } from "components/utils/TagCombobox";
import { toNullIfEmpty } from "utils/formatters";
import { useDialogForm } from "utils/useDialogForm";

function compactNames(names) {
  const clean = (names ?? []).filter(Boolean);
  if (!clean.length) return "—";
  if (clean.length <= 2) return clean.join(", ");
  return `${clean.slice(0, 2).join(", ")} +${clean.length - 2}`;
}

export const GeneralCard = ({ actividad, actividadId, patch, categorias, tipos, nivelesDelSemestre, maps }) => {
  const dialog = useDialogForm({ nombre: "", descripcion: "", tipoActividadId: "", tags: [], niveles: [] });

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;

  const nombre = actividad?.nombre?.trim() || "Actividad sin nombre";
  const descripcion = actividad?.descripcion?.trim() || "Sin descripción";

  const categoriaTxt = useMemo(() => {
    const ids = actividad?.categoriaIds ?? [];
    const names = ids.map((id) => maps.categoriaById.get(id)).filter(Boolean);
    return compactNames(names);
  }, [actividad?.categoriaIds, maps]);

  const tipoTxt = useMemo(() => {
    const id = actividad?.tipoActividadId;
    if (!id) return (tipos ?? []).find((t) => String(t.nombre || "").trim().toLowerCase() === "regular")?.nombre ?? "Regular";
    return maps.tipoById.get(id) ?? "—";
  }, [actividad?.tipoActividadId, maps, tipos]);

  const gradosTxt = useMemo(() => {
    const ids = actividad?.nivelIds ?? [];
    const names = ids.map((id) => maps.nivelById.get(id)).filter(Boolean);
    return (names.length ? names : ["—"]).join(", ");
  }, [actividad?.nivelIds, maps]);

  const tiposCollection = useMemo(() => createListCollection({ items: (tipos ?? []).map((t) => ({ label: t.nombre, value: t.id })) }), [tipos]);
  const nivelesCollection = useMemo(() => createListCollection({ items: (nivelesDelSemestre ?? []).map((n) => ({ label: n.nivelNombre, value: n.nivelId })) }), [nivelesDelSemestre]);
  const defaultTipoActividadId = useMemo(() => {
    const regular = (tipos ?? []).find((t) => String(t.nombre || "").trim().toLowerCase() === "regular");
    return regular?.id ?? "";
  }, [tipos]);

  function openDialog() {
    dialog.openWith({
      nombre: actividad?.nombre ?? "",
      descripcion: actividad?.descripcion ?? "",
      tags: actividad?.categoriaIds ?? [],
      tipoActividadId: actividad?.tipoActividadId ?? defaultTipoActividadId,
      niveles: actividad?.nivelIds ?? [],
    });
  }

  async function save() {
    await patch.mutateAsync({
      actividadId,
      patch: {
        nombre: toNullIfEmpty(dialog.form.nombre),
        descripcion: toNullIfEmpty(dialog.form.descripcion),
        tipoActividadId: toNullIfEmpty(dialog.form.tipoActividadId || defaultTipoActividadId),
        categoriaIds: dialog.form.tags,
        nivelIds: dialog.form.niveles,
      },
    });
    dialog.close();
  }

  return (
    <>
      <DataCard title="Información General" variant="orange" headerIcon={<FaInfoCircle size="24px" />} onEdit={openDialog} editAriaLabel="Editar información general">
        <Stack gap="4" alignItems="start">
          <HStack gap="2.5" align="start">
            <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdSports size="16px" /></Box>
            <Stack gap="0">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Nombre</Text>
              <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{nombre}</Text>
            </Stack>
          </HStack>

          <HStack gap="2.5" align="start">
            <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdComment size="16px" /></Box>
            <Stack gap="0">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Descripción</Text>
              <Text fontSize="sm" color="dashboard.fg.muted">{descripcion}</Text>
            </Stack>
          </HStack>

          <Grid templateColumns="repeat(3, 1fr)" gap="3" alignItems="start">
            <HStack gap="2.5" align="start">
              <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdCategory size="16px" /></Box>
              <Stack gap="0">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Categorías</Text>
                <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{categoriaTxt}</Text>
              </Stack>
            </HStack>

            <HStack gap="2.5" align="start">
              <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><FaHandsHelping size="16px" /></Box>
              <Stack gap="0">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Tipo</Text>
                <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{tipoTxt}</Text>
              </Stack>
            </HStack>

            <HStack gap="2.5" align="start">
              <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><FaUserFriends size="16px" /></Box>
              <Stack gap="0">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Grados</Text>
                <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{gradosTxt}</Text>
              </Stack>
            </HStack>
          </Grid>
        </Stack>
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Información General" errorText={patchError} saving={saving} onCancel={dialog.close} onSave={save}>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Nombre</Text>
          <DashboardInput value={dialog.form.nombre} onChange={(e) => dialog.setField("nombre", e.target.value)} />
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Descripción</Text>
          <DashboardTextarea value={dialog.form.descripcion} onChange={(e) => dialog.setField("descripcion", e.target.value)} rows={5} />
        </Box>

        <Box>
          <TagCombobox label="Categorías" items={(categorias ?? []).map((c) => ({ label: c.nombre, value: c.id }))} value={dialog.form.tags} onChange={(v) => dialog.setField("tags", v)} />
        </Box>

        <HStack gap="2" mt="2" alignItems="center">
          <Select.Root collection={tiposCollection} size="sm" width="320px" value={dialog.form.tipoActividadId ? [dialog.form.tipoActividadId] : []} onValueChange={(e) => dialog.setField("tipoActividadId", e.value?.[0] ?? "")}>
            <Select.HiddenSelect />
            <Select.Label fontSize="sm" fontWeight="600" color="dashboard.fg.default">Tipo</Select.Label>
            <Select.Control>
              <DashboardSelectTrigger>
                <Select.ValueText placeholder="Tipo de Actividad" />
              </DashboardSelectTrigger>
              <Select.IndicatorGroup>
                <Select.Indicator color="dashboard.accent.solid" />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <DashboardSelectContent>
                {tiposCollection.items.map((tipo) => (
                  <DashboardSelectItem item={tipo} key={tipo.value}>
                    {tipo.label}
                    <Select.ItemIndicator />
                  </DashboardSelectItem>
                ))}
              </DashboardSelectContent>
            </Select.Positioner>
          </Select.Root>

          <Select.Root multiple collection={nivelesCollection} size="sm" width="320px" value={dialog.form.niveles} onValueChange={(e) => dialog.setField("niveles", e.value)} positioning={{ strategy: "fixed", hideWhenDetached: true }}>
            <Select.HiddenSelect />
            <Select.Label fontSize="sm" fontWeight="600" color="dashboard.fg.default">Niveles</Select.Label>
            <Select.Control>
              <DashboardSelectTrigger>
                <Select.ValueText placeholder="Niveles de la Act" />
              </DashboardSelectTrigger>
              <Select.IndicatorGroup>
                <Select.ClearTrigger />
                <Select.Indicator color="dashboard.accent.solid" />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <DashboardSelectContent>
                {nivelesCollection.items.map((nivel) => (
                  <DashboardSelectItem item={nivel} key={nivel.value}>
                    {nivel.label}
                    <Select.ItemIndicator />
                  </DashboardSelectItem>
                ))}
              </DashboardSelectContent>
            </Select.Positioner>
          </Select.Root>
        </HStack>
      </FormDialog>
    </>
  );
};
